import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, StatusDot, TableSkeleton, Amount, RowActions, RowAction, ListEmptyRow, StatCard } from '@redpanda/ui';
import { formatTableDate, useRefetchOnFocus, useRememberedState } from '@redpanda/utils';
import { useReceivableStore, Receivable, ReceivableStatus } from '../stores/pmReceivableStore';
import PaymentDialog from '../components/PaymentDialog';
import InvoiceDialog from '../components/InvoiceDialog';
import { useDataChanged } from '../utils/dataEvents';
import { receivableTone, toneOf } from '../utils/statusTone';
import ReceivableCustomersPanel from '../components/ReceivableCustomersPanel';
import ReceivedPaymentsPanel from '../components/ReceivedPaymentsPanel';

type Filter = 'all' | ReceivableStatus;
const FILTERS: Filter[] = ['all', 'PAYMENT_OVERDUE', 'BILLABLE', 'INVOICED', 'OVERDUE', 'UPCOMING'];
/** 分頁：應收帳款（還沒收齊）／依客戶（帳齡＋平均收款天數）／已收款 */
type View = 'open' | 'customers' | 'received';
const VIEWS: View[] = ['open', 'customers', 'received'];

/**
 * 應收帳款：跨專案列出還沒收齊的款項（接案團隊最常漏收的地方），與儀表板「待收款」涵蓋相同的專案
 * 依里程碑收款條件；沒有條件時整個分包一筆，連分包都沒有時整個專案一筆
 * 流程：可請款 →「標記已請款」（請款日＋付款期限）→ 已請款 → 過了預計收款日＝收款逾期（提醒專案負責人）→ 登記收款
 */
const ReceivablesPage: React.FC = () => {
  const { t, i18n } = useTranslation(['translation', 'pages']);
  const { items, summary, lastFetchTime, fetchReceivables } = useReceivableStore();
  const [filter, setFilter] = useRememberedState<Filter>('pm-ui:receivables.filter', 'all', FILTERS);
  const [paying, setPaying] = useState<Receivable | null>(null);
  const [invoicing, setInvoicing] = useState<Receivable | null>(null);
  // 分頁存在網址（?view=，重新整理、分享連結時停在同一頁）；網址沒指定時（從側欄進來）顯示上次看的分頁
  const [searchParams, setSearchParams] = useSearchParams();
  const [rememberedView, rememberView] = useRememberedState<View>('pm-ui:receivables.view', 'open', VIEWS);
  const viewParam = searchParams.get('view') as View | null;
  const view: View = viewParam && VIEWS.includes(viewParam) ? viewParam : rememberedView;
  const setView = (v: View) => {
    rememberView(v);
    setSearchParams(v === 'open' ? {} : { view: v }, { replace: true });
  };

  useEffect(() => { fetchReceivables(); }, [fetchReceivables]);
  useDataChanged(['payments', 'projects', 'packages'], fetchReceivables);
  useRefetchOnFocus(fetchReceivables);

  const visible = filter === 'all' ? items : items.filter(item => item.status === filter);
  const date = (value: string | null) => (value ? formatTableDate(value, i18n.language) : '—');
  const filterClass = (active: boolean) =>
    `inline-flex shrink-0 items-center rounded px-3 py-1.5 text-sm transition-colors ${active ? 'bg-accent-soft text-accent font-medium' : 'text-fg-secondary hover:text-fg'}`;
  const countOf = (f: Filter) => (f === 'all' ? items.length : items.filter(item => item.status === f).length);
  const count = (n?: number) => t('pmReceivables.summary.count', { ns: 'pages', count: n ?? 0 });

  /** 日期欄：收款逾期顯示逾期天數，已請款顯示預計收款日，可請款看完成日，其他看里程碑到期日 */
  const dateCell = (item: Receivable) => {
    if (item.status === 'PAYMENT_OVERDUE') {
      return (
        <span className="text-red-600 dark:text-red-400">
          {t('pmReceivables.overdueDays', { ns: 'pages', count: item.overdueDays })}
          <span className="block text-[13px] tabular-nums">{date(item.paymentDueDate)}</span>
        </span>
      );
    }
    if (item.status === 'INVOICED') return <span className="tabular-nums">{date(item.paymentDueDate)}</span>;
    return (
      <span className={`tabular-nums ${item.status === 'OVERDUE' ? 'text-amber-700 dark:text-amber-400' : ''}`}>
        {date(item.status === 'BILLABLE' ? item.completedDate : item.dueDate)}
      </span>
    );
  };

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('pmReceivables.title', { ns: 'pages' })}</h1>
      </div>

      {/* 分頁籤（同專案詳情頁的底線式） */}
      <nav className="mb-4 flex gap-6 overflow-x-auto scrollbar-hide border-b border-line" aria-label={t('pmReceivables.title', { ns: 'pages' })}>
        {VIEWS.map(v => (
          <button
            key={v}
            type="button"
            onClick={() => setView(v)}
            aria-current={view === v ? 'page' : undefined}
            className={`h-10 -mb-px shrink-0 border-b-2 text-sm transition-colors ${
              view === v ? 'border-accent text-fg font-medium' : 'border-transparent text-fg-secondary hover:text-fg'
            }`}
          >
            {t(`pmReceivables.views.${v}`, { ns: 'pages' })}
          </button>
        ))}
      </nav>

      {view === 'customers' && <ReceivableCustomersPanel />}
      {view === 'received' && <ReceivedPaymentsPanel />}

      {view === 'open' && (<>

      <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title={t('pmReceivables.summary.paymentOverdue', { ns: 'pages' })}
          value={<span className={(summary?.paymentOverdue?.amount ?? 0) > 0 ? 'text-red-600 dark:text-red-400' : ''}><Amount value={summary?.paymentOverdue?.amount ?? 0} /></span>}
          hint={count(summary?.paymentOverdue?.count)}
        />
        <StatCard title={t('pmReceivables.summary.billable', { ns: 'pages' })} value={<Amount value={summary?.billable.amount ?? 0} />} hint={count(summary?.billable.count)} />
        <StatCard title={t('pmReceivables.summary.invoiced', { ns: 'pages' })} value={<Amount value={summary?.invoiced?.amount ?? 0} />} hint={count(summary?.invoiced?.count)} />
        <StatCard title={t('pmReceivables.summary.dueSoon', { ns: 'pages' })} value={<Amount value={summary?.dueSoon.amount ?? 0} />} hint={count(summary?.dueSoon.count)} />
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <nav aria-label={t('pmReceivables.filterLabel', { ns: 'pages' })} className="inline-flex h-10 max-w-full items-center overflow-x-auto scrollbar-hide rounded-md border border-line bg-surface p-0.5">
          {FILTERS.map(f => (
            <button key={f} type="button" onClick={() => setFilter(f)} aria-pressed={filter === f} className={filterClass(filter === f)}>
              {f === 'all' ? t('pmReceivables.filter.all', { ns: 'pages' }) : t(`pmReceivables.status.${f}`, { ns: 'pages' })}
              <span className="ml-1.5 tabular-nums text-fg-muted">{countOf(f)}</span>
            </button>
          ))}
        </nav>
        {/* 英日文提示較長：在篩選列右邊剩下的寬度內換行，不超出畫面 */}
        <p className="min-w-0 flex-1 text-[13px] text-fg-secondary sm:text-right">{t('pmReceivables.hint', { ns: 'pages' })}</p>
      </div>

      {lastFetchTime === null ? (
        <TableSkeleton rows={6} columns={8} />
      ) : (
        <div className="bg-surface shadow dark:shadow-none dark:border dark:border-line overflow-hidden rounded-lg">
          <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <Table style={{ tableLayout: 'fixed', minWidth: '1220px' }}>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-40">{t('pmReceivables.fields.status', { ns: 'pages' })}</TableHead>
                  <TableHead>{t('pmReceivables.fields.project', { ns: 'pages' })}</TableHead>
                  <TableHead>{t('pmReceivables.fields.milestone', { ns: 'pages' })}</TableHead>
                  <TableHead numeric className="w-44">{t('pmReceivables.fields.date', { ns: 'pages' })}</TableHead>
                  <TableHead numeric className="w-32">{t('pmReceivables.fields.expected', { ns: 'pages' })}</TableHead>
                  <TableHead numeric className="w-32">{t('pmReceivables.fields.received', { ns: 'pages' })}</TableHead>
                  <TableHead numeric className="w-32">{t('pmReceivables.fields.outstanding', { ns: 'pages' })}</TableHead>
                  <TableHead className="w-60">{t('common.table.columns.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.length === 0 ? (
                  <ListEmptyRow
                    colSpan={8}
                    filtered={false}
                    title={t(filter === 'all' ? 'pmReceivables.empty' : 'pmReceivables.emptyFiltered', { ns: 'pages' })}
                    hint={filter === 'all' ? t('pmReceivables.emptyHint', { ns: 'pages' }) : undefined}
                  />
                ) : (
                  visible.map(item => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <StatusDot tone={toneOf(receivableTone, item.status)} label={t(`pmReceivables.status.${item.status}`, { ns: 'pages' })} />
                      </TableCell>
                      <TableCell className="truncate">
                        <Link to={`/projects/${item.projectId}?tab=payments`} className="font-medium text-fg hover:text-accent hover:underline">
                          {item.projectName}
                        </Link>
                      </TableCell>
                      <TableCell className="whitespace-normal">
                        {/* 依里程碑：里程碑＋所屬分包小字；整包：分包名稱；整個專案：「整個專案」 */}
                        <div className="text-fg">{item.milestoneName ?? item.packageName ?? t('pmReceivables.wholeProject', { ns: 'pages' })}</div>
                        {item.milestoneName && item.packageName && <div className="mt-0.5 text-[13px] text-fg-secondary">{item.packageName}</div>}
                      </TableCell>
                      <TableCell numeric>{dateCell(item)}</TableCell>
                      <TableCell numeric><Amount value={item.expected} /></TableCell>
                      <TableCell numeric><Amount value={item.received} /></TableCell>
                      <TableCell numeric className="font-semibold"><Amount value={item.outstanding} /></TableCell>
                      <TableCell>
                        <RowActions>
                          <RowAction onClick={() => setPaying(item)}>{t('pmReceivables.recordPayment', { ns: 'pages' })}</RowAction>
                          <RowAction onClick={() => setInvoicing(item)}>
                            {t(item.invoicedAt ? 'pmReceivables.invoice.edit' : 'pmReceivables.invoice.mark', { ns: 'pages' })}
                          </RowAction>
                        </RowActions>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
      </>)}

      {paying && (
        <PaymentDialog
          isOpen
          onClose={() => setPaying(null)}
          projectId={paying.projectId}
          milestones={paying.milestoneId ? [{ id: paying.milestoneId, name: paying.milestoneName ?? '' }] : []}
          packages={paying.packageId ? [{ id: paying.packageId, name: paying.packageName ?? '' }] : []}
          defaults={{ milestoneId: paying.milestoneId ?? undefined, packageId: paying.packageId ?? undefined, amount: paying.outstanding }}
        />
      )}
      {invoicing && <InvoiceDialog item={invoicing} onClose={() => setInvoicing(null)} />}
    </div>
  );
};

export default ReceivablesPage;
